import React, {forwardRef, useCallback} from 'react'
import { flexRender } from '@tanstack/react-table'
import { TinyColumnChooser } from '../../XtractTableUtils.jsx'
import { FiltersRow } from '../Filters/Filters.jsx'
import { escapeForTooltipHtml, formatFilterValue, isFilterValueEmpty } from '../Filters/formatFilterValue.js'
import ExpandedRow from './ExpandedRow.jsx'
import DummyRows from './DummyRows.jsx'
import _ from 'lodash'

const Table = forwardRef(
  (
    {
      table,
      manual,
      showFilters,
      renderSubComponent,
      getTdProps = null,
      getTrProps = null,
      getTheadTrProps = props => {
        return props
      },
      getTheadThProps = (props, column) => {
        return props
      },
      columns,
      showTinyColumnChooser,
      showControls,
      rowOnClickAction,
      columnVisibility,
      setColumnVisibility,
      pageSize,
      noDataText = 'no records available',
      useDummyRows = true,
      onImmediateFilterChange,
    },
    ref
  ) => {
    const showColumnChooser = showTinyColumnChooser !== false && !showControls

    const {
      getHeaderGroups,
      getRowModel,
      resetExpanded,
      resetRowSelection,
      getSelectedRowModel
    } = table

    const onRowClick = useCallback(
      row => {
        switch (rowOnClickAction) {
          case 'single-select': {
            resetRowSelection(true)
            row.toggleSelected()
            break
          }
          case 'single-select-sticky': {
            if (!getSelectedRowModel().rowsById[row.id]) {
              resetRowSelection(true)
              row.toggleSelected()
            }
            break
          }
          case 'multi-select': {
            row.toggleSelected()
            break
          }
          case 'single-expand': {
            if (row.getCanExpand()) {
              const wasExpanded = row.getIsExpanded()
              resetExpanded(false)
              row.toggleExpanded(!wasExpanded)
            }
            break
          }
          case 'multi-expand': {
            if (row.getCanExpand()) {
              row.toggleExpanded()
            }
            break
          }
        }
      },
      [rowOnClickAction, resetExpanded, resetRowSelection, getSelectedRowModel]
    )

    return (
      <React.Fragment>
        <table ref={ref}>
          <colgroup>
            {getHeaderGroups().map(headerGroup =>
              headerGroup.headers
                .filter(header => header.column.columnDef.showable !== false)
                .map(header => {
                  return <col key={header.id} />
                })
            )}
          </colgroup>
          <thead>
            {
              // the header rows...
              getHeaderGroups().map(headerGroup => (
                <React.Fragment key={headerGroup.id}>
                  <tr {...getTheadTrProps({ key: headerGroup.id })}>
                    {headerGroup.headers
                      .filter(
                        header => header.column.columnDef.showable !== false
                      )
                      .map((header, i) => {
                        const width = !isNaN(header.column.getSize())
                          ? header.column.getSize()
                          : '100%'
                        const minWidth = header.column.columnDef.minSize
                        // A column may opt out of the "filtered" indicator for
                        // values that represent an effectively-unfiltered state
                        // (e.g. a multi-select with every option chosen) by
                        // providing columnDef.isFilterCleared(value). We also
                        // treat empty containers as cleared by default, since a
                        // cleared date-range filter stores an empty object that
                        // TanStack does not auto-remove from columnFilters.
                        const filterValue = header.column.getFilterValue?.()
                        const isFiltered =
                          (header.column.getIsFiltered?.() ?? false) &&
                          !isFilterValueEmpty(filterValue) &&
                          !(header.column.columnDef?.isFilterCleared?.(filterValue) ?? false)
                        // Columns whose filter value is a primitive (e.g. a
                        // DropdownMenu filter that stores 'y'/'n'/'either')
                        // can opt into a custom display via
                        // columnDef.formatFilterValue(value, column).
                        const formatValue = header.column.columnDef?.formatFilterValue
                        const formattedValue = formatValue
                          ? formatValue(filterValue, header.column)
                          : formatFilterValue(filterValue, header.column.id)
                        const filterTooltip = isFiltered
                          ? `Filtered: ${escapeForTooltipHtml(formattedValue)}`
                          : null
                        return (
                          <th
                            key={header.id}
                            {...getTheadThProps(
                              {
                                colSpan: header.colSpan,
                                style: {
                                  width,
                                  ...(minWidth ? { minWidth } : {})
                                }
                              },
                              header.column
                            )}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={'column-header'}
                                data-toggle={isFiltered ? 'tooltip' : null}
                                data-title={filterTooltip}
                                data-placement={isFiltered ? 'top' : null}
                              >
                                {i === 0 && showColumnChooser === true ? (
                                  <TinyColumnChooser
                                    visible={columnVisibility}
                                    columns={columns}
                                    includeSelectAll={true}
                                    onChange={setColumnVisibility}
                                  />
                                ) : null}
                                <div
                                  {...{
                                    className:
                                      'header-div-text ' +
                                      (isFiltered ? 'filtered ' : '') +
                                      (header.column.getCanSort()
                                        ? 'cursor-pointer select-none'
                                        : '') +
                                      ' ' +
                                      ({
                                        asc: 'sorted-asc',
                                        desc: 'sorted-desc'
                                      }[header.column.getIsSorted()] ?? ''),
                                    onClick:
                                      header.column.getToggleSortingHandler()
                                  }}
                                >
                                  <div
                                    className={
                                      header.column?.columnDef?.classes
                                    }
                                  >
                                    {
                                      // render header contents
                                      flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )
                                    }
                                  </div>
                                </div>
                              </div>
                            )}
                            {
                              // don't show resize handle for fixedSize columns
                              header.column.getCanResize() ? (
                                <div
                                  onMouseDown={header.getResizeHandler()}
                                  onTouchStart={header.getResizeHandler()}
                                  className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                                />
                              ) : null
                            }
                          </th>
                        )
                      })}
                  </tr>
                  {showFilters ? (
                    <FiltersRow
                      headerGroup={headerGroup}
                      manual={manual}
                      table={table}
                      onImmediateFilterChange={onImmediateFilterChange}
                    />
                  ) : null}
                </React.Fragment>
              ))
            }
          </thead>
          <tbody>
            {
              // the data rows...
              getRowModel().rows.map(row => {
                const trProps =
                  getTrProps === null
                    ? {
                        onClick: () => {
                          onRowClick(row)
                        },
                        className: row.getIsSelected() ? 'selected-row' : ''
                      }
                    : getTrProps(
                        row,
                        {
                          onClick: () => {
                            onRowClick(row)
                          },
                          className: row.getIsSelected() ? 'selected-row' : ''
                        },
                        false
                      )
                return (
                  <React.Fragment key={row.id}>
                    <tr key={row.id} {...trProps}>
                      {row
                        .getVisibleCells()
                        .filter(
                          cell => cell.column.columnDef.showable !== false
                        )
                        .map(cell => {
                          const tdProps =
                            getTdProps === null
                              ? {
                                  style: {
                                    width: !isNaN(cell.column.getSize())
                                      ? cell.column.getSize()
                                      : '100%'
                                  }
                                }
                              : getTdProps(
                                  row,
                                  {
                                    style: {
                                      width: !isNaN(cell.column.getSize())
                                        ? cell.column.getSize()
                                        : '100%'
                                    }
                                  },
                                  cell.column
                                )
                          return (
                            <td key={cell.id} {...tdProps}>
                              {
                                // render cell contents
                                // oddly, if the cell is produced by a function, we need to let it render directly
                                // into the DOM; otherwise, buttons and such lose their onClick events
                                // still don't know why
                                <div
                                  className={cell.column?.columnDef?.classes}
                                >
                                  {typeof cell.column.columnDef.cell ===
                                  'function'
                                    ? cell.column.columnDef.cell(
                                        cell.getContext()
                                      )
                                    : flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      )}
                                </div>
                              }
                            </td>
                          )
                        })}
                    </tr>
                    <ExpandedRow
                      row={row}
                      renderSubComponent={renderSubComponent}
                      getTrProps={getTrProps}
                    />
                  </React.Fragment>
                )
              })
            }
            {useDummyRows ? (
              <DummyRows
                numRows={Math.floor(pageSize) - getRowModel().rows.length}
                getHeaderGroups={getHeaderGroups}
              />) : null
            }


          </tbody>
        </table>
        {getRowModel().rows.length || !noDataText ? null : (
          <div className={'float-over-table'}>
            <div className={'floating-message'}>{noDataText}</div>
          </div>
        )}
      </React.Fragment>
    )
  }
)

export default Table
